import { checkMinMax } from 'aos-helpers/src/helpers/Number'
import {
    translateParts,
    translatePartsOptional,
} from 'aos-helpers/src/helpers/translations/Translations'
import { AnimalsTaskFormPayload } from 'aos-services/src/services/tasks/types/payload/AnimalsTaskFormPayload'
import {
    allAnimalAges,
    allAnimalGenders,
    allAnimalsTaskTypes,
    allAnimalTypes,
    AnimalSpecie,
    AnimalsTaskType,
    AnimalType,
    translateAnimalAge,
    translateAnimalGender,
    translateAnimalsTaskType,
    translateAnimalType,
} from 'aos-services/src/services/tasks/types/task/AnimalsTask'
import {
    animalSpeciesForType,
    TaskMetadata,
} from 'aos-services/src/services/tasks/types/TaskMetadata'
import { Box, Dropdown, NumberInput, Switch } from 'aos-ui-universal'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { Controller, useController, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { animalTaskTypeIcons } from '../../../utils'
import { TaskFormField } from '../TaskFormField'
import { TaskOptionChip } from '../TaskOptionChip'

interface AnimalsTaskFormProps {
    metadata: TaskMetadata
    isTaskTemplate: boolean
}

export const AnimalsTaskForm: FC<AnimalsTaskFormProps> = ({ metadata, isTaskTemplate }) => {
    const { t: translate } = useTranslation()
    const { control } = useFormContext<AnimalsTaskFormPayload>()
    const payload = useWatch({ control }) as AnimalsTaskFormPayload
    const titleField = useController({ name: 'title', control })
    const [animalType, setAnimalType] = useState<AnimalType | undefined>(
        payload.animalSpecies?.animalType,
    )
    useEffect(() => {
        queueMicrotask(() => {
            if (payload?.animalSpecies?.animalType) {
                setAnimalType(payload.animalSpecies.animalType)
            }
        })
    }, [payload?.animalSpecies?.animalType])
    const species = useMemo(
        () => animalSpeciesForType(metadata, animalType),
        [metadata, animalType],
    )

    const areCartridgesRequired = [AnimalsTaskType.Banishment, AnimalsTaskType.Kill].some(
        t => t === payload.animalTaskType,
    )

    const areExpulsionsRequired = payload.animalTaskType === AnimalsTaskType.Banishment

    const spikesError = (hasError: boolean, value?: number) => {
        if (!checkMinMax(value, 0, 50)) {
            return translate('form.animal-spikes.min-max-error', { ns: 'tasks' })
        }
        return hasError ? translate('form.animal-spikes.error', { ns: 'tasks' }) : undefined
    }

    const isMammalToKill =
        animalType === AnimalType.Mammal &&
        payload.animalTaskType === AnimalsTaskType.Kill &&
        payload.animalSpecies?.horns

    const killMammalForm = () => (
        <>
            <Controller
                control={control}
                name='gender'
                render={({ field, fieldState }) => (
                    <TaskFormField
                        label={translate('form.animal-gender.label', { ns: 'tasks' })}
                        required={!isTaskTemplate}
                        error={
                            fieldState.error
                                ? translate('form.animal-gender.error', { ns: 'tasks' })
                                : undefined
                        }
                        seleniumLocation='category'
                    >
                        <div className='grid grid-cols-4 gap-2'>
                            {allAnimalGenders.map(item => (
                                <TaskOptionChip
                                    key={item}
                                    label={translateAnimalGender(translate)(item)}
                                    isSelected={field.value === item}
                                    onPress={() => field.onChange(item)}
                                />
                            ))}
                        </div>
                    </TaskFormField>
                )}
            />
            <Controller
                control={control}
                name='lifeStage'
                render={({ field, fieldState }) => (
                    <TaskFormField
                        label={translate('form.animal-age.label', { ns: 'tasks' })}
                        required={!isTaskTemplate}
                        error={
                            fieldState.error
                                ? translate('form.animal-age.error', { ns: 'tasks' })
                                : undefined
                        }
                        seleniumLocation='category'
                    >
                        <div className='grid grid-cols-4 gap-2'>
                            {allAnimalAges.map(item => (
                                <TaskOptionChip
                                    key={item}
                                    label={translateAnimalAge(translate)(item)}
                                    isSelected={field.value === item}
                                    onPress={() => field.onChange(item)}
                                />
                            ))}
                        </div>
                    </TaskFormField>
                )}
            />
            <Controller
                control={control}
                name='horns'
                render={({ field }) => (
                    <TaskFormField label={translate('form.animal-horns.label', { ns: 'tasks' })}>
                        <Switch value={!!field.value} onValueChange={field.onChange} />
                    </TaskFormField>
                )}
            />
            {payload.horns && (
                <Controller
                    control={control}
                    name='amountOfSpikes'
                    render={({ field, fieldState }) => (
                        <TaskFormField
                            label={translate('form.animal-spikes.label', { ns: 'tasks' })}
                            required={payload.horns && !isTaskTemplate}
                            error={spikesError(!!fieldState.error, field.value)}
                        >
                            <NumberInput
                                value={field.value}
                                onChange={field.onChange}
                                isInvalid={!!spikesError(!!fieldState.error, field.value)}
                                placeholder={translate('form.animal-spikes.placeholder', {
                                    ns: 'tasks',
                                })}
                            />
                        </TaskFormField>
                    )}
                />
            )}
        </>
    )

    return (
        <>
            <Controller
                control={control}
                name='animalTaskType'
                render={({ field, fieldState }) => (
                    <TaskFormField
                        label={translate('form.animal-task-type.label', { ns: 'tasks' })}
                        required={!isTaskTemplate}
                        error={
                            fieldState.error
                                ? translate('form.animal-task-type.error', { ns: 'tasks' })
                                : undefined
                        }
                        seleniumLocation='category'
                    >
                        <div className='grid grid-cols-4 gap-2'>
                            {allAnimalsTaskTypes.map(item => (
                                <TaskOptionChip
                                    key={item}
                                    orientation='vertical'
                                    icon={animalTaskTypeIcons[item as AnimalsTaskType]}
                                    label={translateAnimalsTaskType(translate)(item)}
                                    isSelected={field.value === item}
                                    onPress={() => field.onChange(item)}
                                />
                            ))}
                        </div>
                    </TaskFormField>
                )}
            />
            <Box className='shrink-0 flex-row items-start'>
                {areCartridgesRequired && (
                    <Box className='shrink-0 flex-1'>
                        <Controller
                            control={control}
                            name='cartridges'
                            render={({ field, fieldState }) => (
                                <TaskFormField
                                    label={translate('form.animal-cartridges.label', {
                                        ns: 'tasks',
                                    })}
                                    required={!isTaskTemplate}
                                    error={
                                        fieldState.error
                                            ? translate('form.animal-cartridges.error', {
                                                  ns: 'tasks',
                                              })
                                            : undefined
                                    }
                                    seleniumLocation='animal-cartridges'
                                    className='mb-3'
                                >
                                    <NumberInput
                                        value={field.value}
                                        onChange={field.onChange}
                                        isInvalid={!!fieldState.error}
                                        placeholder={translate(
                                            'form.animal-cartridges.placeholder',
                                            {
                                                ns: 'tasks',
                                            },
                                        )}
                                    />
                                </TaskFormField>
                            )}
                        />
                    </Box>
                )}
                {areExpulsionsRequired && (
                    <Box className='shrink-0 flex-1 ml-5'>
                        <Controller
                            control={control}
                            name='expulsions'
                            render={({ field, fieldState }) => (
                                <TaskFormField
                                    label={translate('form.animal-expulsions.label', {
                                        ns: 'tasks',
                                    })}
                                    required={!isTaskTemplate}
                                    error={
                                        fieldState.error
                                            ? translate('form.animal-expulsions.error', {
                                                  ns: 'tasks',
                                              })
                                            : undefined
                                    }
                                    seleniumLocation='animal-expulsions'
                                    className='mb-3'
                                >
                                    <NumberInput
                                        value={field.value}
                                        onChange={field.onChange}
                                        isInvalid={!!fieldState.error}
                                        placeholder={translate(
                                            'form.animal-expulsions.placeholder',
                                            {
                                                ns: 'tasks',
                                            },
                                        )}
                                    />
                                </TaskFormField>
                            )}
                        />
                    </Box>
                )}
            </Box>

            <Box className='shrink-0 mb-3'>
                <TaskFormField label={translateParts(translate)('tasks.form.animal-type', 'label')}>
                    <Dropdown<AnimalType>
                        items={allAnimalTypes}
                        value={animalType ?? null}
                        labelRenderer={translateAnimalType(translate)}
                        placeholder={translatePartsOptional(translate)(
                            'tasks.form.animal-type',
                            'placeholder',
                        )}
                        onChange={t => setAnimalType(t ?? undefined)}
                    />
                </TaskFormField>
            </Box>

            <Box className='shrink-0 flex-row items-start'>
                <Box className='shrink-0 flex-[2] mb-3'>
                    <Controller
                        control={control}
                        name='animalSpecies'
                        render={({ field, fieldState }) => (
                            <TaskFormField
                                label={translateParts(translate)(
                                    'tasks.form.animal-species',
                                    'label',
                                )}
                                required={!isTaskTemplate}
                                error={
                                    fieldState.error
                                        ? translatePartsOptional(translate)(
                                              'tasks.form.animal-species',
                                              'error',
                                          )
                                        : undefined
                                }
                            >
                                <Dropdown<AnimalSpecie>
                                    items={species}
                                    value={field.value ?? null}
                                    labelRenderer={(v: AnimalSpecie) => v.label}
                                    placeholder={translatePartsOptional(translate)(
                                        'tasks.form.animal-species',
                                        'placeholder',
                                    )}
                                    isInvalid={!!fieldState.error}
                                    onChange={animalSpecies => {
                                        field.onChange(animalSpecies ?? undefined)
                                        titleField.field.onChange(animalSpecies?.label)
                                    }}
                                />
                            </TaskFormField>
                        )}
                    />
                </Box>
                <Box className='shrink-0 flex-1 ml-5 mb-3'>
                    <Controller
                        control={control}
                        name='amount'
                        render={({ field, fieldState }) => (
                            <TaskFormField
                                label={translateParts(translate)(
                                    'tasks.form.animal-amount',
                                    'label',
                                )}
                                required={!isTaskTemplate}
                                error={
                                    fieldState.error
                                        ? translatePartsOptional(translate)(
                                              'tasks.form.animal-amount',
                                              'error',
                                          )
                                        : undefined
                                }
                            >
                                <NumberInput
                                    value={field.value}
                                    onChange={field.onChange}
                                    isInvalid={!!fieldState.error}
                                    placeholder={translatePartsOptional(translate)(
                                        'tasks.form.animal-amount',
                                        'placeholder',
                                    )}
                                />
                            </TaskFormField>
                        )}
                    />
                </Box>
            </Box>

            {isMammalToKill && killMammalForm()}
        </>
    )
}
